// Deterministic castle-wall auto-gates. Friendly crossing traffic requests the doors, but the
// aperture is shared physical space once open: opponents may pass until it safely closes.

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';
import { footprintOf, type Building, type Squad, type WorkerEnt } from '../state';

export interface WallGateRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const BODY_RADIUS = {
  small: 0.6,
  medium: 0.85,
  large: 1.5,
  giant: 2.7,
} as const;

/** Shared aperture geometry used by nav stamping, traffic sensing, and presentation. */
export function wallGateRect(wall: Building): WallGateRect | null {
  const def = buildingDef(wall.defId);
  if (def.kind !== 'wall' || wall.gateSpan <= 0) return null;
  const fp = footprintOf(def, wall.rot);
  const span = Math.min(wall.gateSpan, Math.max(fp.w, fp.h) - 2);
  if (span <= 0) return null;
  if (fp.h >= fp.w) {
    return {
      x0: wall.pos.x - fp.w / 2,
      y0: wall.pos.y - span / 2,
      x1: wall.pos.x + fp.w / 2,
      y1: wall.pos.y + span / 2,
    };
  }
  return {
    x0: wall.pos.x - span / 2,
    y0: wall.pos.y - fp.h / 2,
    x1: wall.pos.x + span / 2,
    y1: wall.pos.y + fp.h / 2,
  };
}

function distanceToRect(pos: Vec2, rect: WallGateRect): number {
  const dx = Math.max(rect.x0 - pos.x, 0, pos.x - rect.x1);
  const dy = Math.max(rect.y0 - pos.y, 0, pos.y - rect.y1);
  return Math.hypot(dx, dy);
}

export function squadBodyRadius(squad: Squad): number {
  return BODY_RADIUS[unitDef(squad.defId).sizeTag];
}

function activeSquad(squad: Squad): boolean {
  return !squad.dead && squad.mountedOnWall === null && squad.mountedOnSquad === null;
}

function activeWorker(worker: WorkerEnt): boolean {
  return !worker.dead && worker.state !== 'sheltered';
}

function targetPosition(sim: Sim, id: number | undefined): Vec2 | null {
  if (id === undefined) return null;
  return (
    sim.state.squads.get(id)?.pos ??
    sim.state.workers.get(id)?.pos ??
    sim.state.buildings.get(id)?.pos ??
    sim.state.mines.get(id)?.pos ??
    null
  );
}

/** Current destination implied by a squad's high-level order (idle guards return null). */
function squadTrafficGoal(sim: Sim, squad: Squad): Vec2 | null {
  // The previous completed command tick is authoritative: Defend, Escort, and combat
  // compute real id-derived slots that their high-level command fields cannot reproduce.
  if (squad.moveTarget) return squad.moveTarget;
  if (squad.command.kind === 'idle') return null;
  // A fresh queued order reaches gates before stepCommands in this tick. Only that one
  // pre-FSM window may use command fallbacks; a stationary guard must not reopen a gate.
  if (squad.commandIssuedTick !== sim.state.tick) return null;
  const command = squad.command;
  switch (command.kind) {
    case 'move':
    case 'raid':
      return command.targetPos ?? null;
    case 'attack':
      return targetPosition(sim, command.targetEntity) ?? command.targetPos ?? null;
    case 'capture':
      return command.targetPos ??
        sim.state.sites.find((site) => site.id === command.siteId)?.pos ??
        null;
    case 'defend':
      return command.pointA ?? null;
    case 'patrol':
      return (command.patrolLegToB ? command.pointB : command.pointA) ?? null;
    case 'escort':
      return targetPosition(sim, command.targetEntity ?? squad.escortOf ?? undefined);
    case 'withdraw': {
      // Match commandsHl's deterministic nearest-safe-area choice. This also lets a newly
      // issued Withdraw open a gate on its first tick, before moveTarget has been refreshed.
      const candidates: Vec2[] = [];
      const core = sim.coreAlive(squad.owner);
      if (core) candidates.push(core.pos);
      for (const site of sim.state.sites) if (site.owner === squad.owner) candidates.push(site.pos);
      let best: Vec2 | null = null;
      let bestDistance = Infinity;
      const lowHealth = squad.health / squad.maxHealth < 0.25;
      for (const candidate of candidates) {
        let score = dist(squad.pos, candidate);
        if (core && candidate === core.pos && lowHealth) score *= 0.7;
        if (score < bestDistance) {
          best = candidate;
          bestDistance = score;
        }
      }
      return best;
    }
    case 'idle':
      return null;
  }
}

/** Current destination implied by a worker FSM state. Stationary work states return null. */
function workerTrafficGoal(sim: Sim, worker: WorkerEnt): Vec2 | null {
  const pathGoal = worker.path?.[worker.path.length - 1];
  if (pathGoal) return pathGoal;
  switch (worker.state) {
    case 'toDeposit':
      return worker.depositId === null ? null : (sim.state.deposits.get(worker.depositId)?.pos ?? null);
    case 'toDropOff':
    case 'toShelter':
      return worker.homeId === null ? null : (sim.state.buildings.get(worker.homeId)?.pos ?? null);
    case 'toBuild':
      return worker.buildTargetId === null ? null : (sim.state.buildings.get(worker.buildTargetId)?.pos ?? null);
    case 'toRepair':
      return worker.repairTargetId === null ? null : (sim.state.buildings.get(worker.repairTargetId)?.pos ?? null);
    case 'fleeing':
      return null;
    default:
      return null;
  }
}

/** Does a planned segment cross the gate's normal plane inside the visible aperture? */
function segmentCrossesGate(a: Vec2, b: Vec2, rect: WallGateRect, bodyRadius: number): boolean {
  const verticalWall = rect.y1 - rect.y0 >= rect.x1 - rect.x0;
  const normalCenter = verticalWall ? (rect.x0 + rect.x1) / 2 : (rect.y0 + rect.y1) / 2;
  const tangentCenter = verticalWall ? (rect.y0 + rect.y1) / 2 : (rect.x0 + rect.x1) / 2;
  const fromNormal = (verticalWall ? a.x : a.y) - normalCenter;
  const toNormal = (verticalWall ? b.x : b.y) - normalCenter;
  const deltaNormal = toNormal - fromNormal;
  if (Math.abs(deltaNormal) < 1e-6 || fromNormal * toNormal > 0) return false;
  const t = -fromNormal / deltaNormal;
  if (t < 0 || t > 1) return false;
  const fromTangent = verticalWall ? a.y : a.x;
  const toTangent = verticalWall ? b.y : b.x;
  const crossingTangent = fromTangent + (toTangent - fromTangent) * t;
  const apertureHalf = verticalWall ? (rect.y1 - rect.y0) / 2 : (rect.x1 - rect.x0) / 2;
  return Math.abs(crossingTangent - tangentCenter) <= apertureHalf + bodyRadius + 0.5;
}

function routeCrossesGate(
  pos: Vec2,
  path: Vec2[] | null,
  pathIndex: number,
  goal: Vec2,
  rect: WallGateRect,
  bodyRadius: number,
): boolean {
  let from = pos;
  if (path) {
    for (let i = pathIndex; i < path.length; i++) {
      const waypoint = path[i];
      if (segmentCrossesGate(from, waypoint, rect, bodyRadius)) return true;
      from = waypoint;
    }
  }
  return segmentCrossesGate(from, goal, rect, bodyRadius);
}

/** True when an actor's visible body overlaps the aperture. Used before first nav stamp. */
export function gateApertureOccupied(sim: Sim, wall: Building): boolean {
  const rect = wallGateRect(wall);
  if (!rect) return false;
  for (const squad of sim.state.squads.values()) {
    if (activeSquad(squad) && distanceToRect(squad.pos, rect) <= squadBodyRadius(squad)) return true;
  }
  for (const worker of sim.state.workers.values()) {
    if (activeWorker(worker) && distanceToRect(worker.pos, rect) <= 0.45) return true;
  }
  return false;
}

function friendlyRequestsOpen(sim: Sim, wall: Building, rect: WallGateRect): boolean {
  for (const squad of sim.state.squads.values()) {
    if (!activeSquad(squad) || squad.owner !== wall.owner) continue;
    const radius = squadBodyRadius(squad);
    if (distanceToRect(squad.pos, rect) > TUNING.wallGateTriggerDistance + radius) continue;
    const goal = squadTrafficGoal(sim, squad);
    if (!goal || squad.rootedRemaining > 0) continue;
    if (routeCrossesGate(squad.pos, squad.path, squad.pathIndex, goal, rect, radius)) return true;
  }
  for (const worker of sim.state.workers.values()) {
    if (!activeWorker(worker) || worker.owner !== wall.owner) continue;
    if (distanceToRect(worker.pos, rect) > TUNING.wallGateTriggerDistance + 0.45) continue;
    const goal = workerTrafficGoal(sim, worker);
    if (goal && routeCrossesGate(worker.pos, worker.path, worker.pathIndex, goal, rect, 0.45)) return true;
  }
  return false;
}

/** Closing clearance includes full formation silhouettes, not just entity center points. */
function apertureClear(sim: Sim, rect: WallGateRect): boolean {
  for (const squad of sim.state.squads.values()) {
    if (!activeSquad(squad)) continue;
    const clearance = TUNING.wallGateClearanceDistance + squadBodyRadius(squad);
    if (distanceToRect(squad.pos, rect) <= clearance) return false;
  }
  for (const worker of sim.state.workers.values()) {
    if (!activeWorker(worker)) continue;
    if (distanceToRect(worker.pos, rect) <= TUNING.wallGateClearanceDistance + 0.45) return false;
  }
  return true;
}

export function stepGates(sim: Sim): void {
  const dt = TUNING.dt;
  for (const wall of sim.state.buildings.values()) {
    if (wall.state !== 'active' || !wall.navStamped) continue;
    const rect = wallGateRect(wall);
    if (!rect) continue;

    const friendlyApproach = friendlyRequestsOpen(sim, wall, rect);
    const clear = apertureClear(sim, rect);
    if (friendlyApproach) {
      wall.gateOpen = true;
      wall.gateHoldRemaining = TUNING.wallGateHoldSeconds;
    } else if (wall.gateOpen) {
      // Anyone still in or approaching the aperture holds the shared doors. This prevents
      // a closing leaf from stamping over an enemy who entered while it was physically open.
      if (!clear) wall.gateHoldRemaining = TUNING.wallGateHoldSeconds;
      else wall.gateHoldRemaining = Math.max(0, wall.gateHoldRemaining - dt);
      if (wall.gateHoldRemaining <= 0) wall.gateOpen = false;
    }

    // Shared traffic can arrive after closing begins. Reverse immediately, before a door
    // leaf visually overlaps the formation; do not wait for the collision threshold.
    if (!wall.gateOpen && wall.gateOpenFraction > 0 && !clear) {
      wall.gateOpen = true;
      wall.gateHoldRemaining = TUNING.wallGateHoldSeconds;
    }

    if (wall.gateOpen) {
      wall.gateOpenFraction = Math.min(
        1,
        wall.gateOpenFraction + dt / TUNING.wallGateOpenSeconds,
      );
      // Door leaves are mostly clear before shared physical navigation opens.
      if (!wall.gateNavOpen && wall.gateOpenFraction >= 0.8) {
        sim.setWallGateNavOpen(wall, true);
      }
    } else {
      wall.gateOpenFraction = Math.max(
        0,
        wall.gateOpenFraction - dt / TUNING.wallGateCloseSeconds,
      );
      if (wall.gateNavOpen && wall.gateOpenFraction <= 0.15) {
        if (clear) sim.setWallGateNavOpen(wall, false);
        else {
          // Traffic arrived during the closing animation: reverse without ever stamping it.
          wall.gateOpen = true;
          wall.gateHoldRemaining = TUNING.wallGateHoldSeconds;
        }
      }
    }
  }
}
